import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Loader2, Info, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';

/* ─────────────────────────────────────────────────────────────────────
 * Iter 62.5 — Onboarding fallback banner (structured reasons).
 *
 * When onboarding short-circuits to a Demo Lead (Cloudflare 403, Apollo
 * no-match, ICP failure, etc.) the backend now persists a structured
 * `fallback_reason` on the onboarding_jobs doc. /api/onboarding/status
 * exposes it as `last_job.fallback_reason = { code, title, message, hint,
 * severity }` so we render the SPECIFIC reason instead of a generic
 * "we couldn't read your website" message.
 *
 * Visibility:
 *   - last_job has a fallback_reason   AND
 *   - tenant has any is_demo lead      AND
 *   - tenant has 0 non-demo leads
 *   - not dismissed (per-tenant localStorage)
 * ───────────────────────────────────────────────────────────────────── */

const DISMISS_KEY = 'onboarding_fallback_dismissed_v2';

// Color palette per severity — keeps the banner aesthetic consistent with shadcn.
const SEVERITY_STYLES = {
  info: {
    border: 'border-sky-300/60 dark:border-sky-700/40',
    bg: 'bg-sky-50/80 dark:bg-sky-950/30',
    iconBg: 'bg-sky-500/15',
    icon: 'text-sky-600 dark:text-sky-400',
    heading: 'text-sky-900 dark:text-sky-200',
    body: 'text-sky-800/90 dark:text-sky-300/80',
    code: 'bg-sky-100/70 dark:bg-sky-900/40',
    cta: 'bg-sky-600 hover:bg-sky-500',
    Icon: Info,
  },
  warning: {
    border: 'border-amber-300/60 dark:border-amber-700/40',
    bg: 'bg-amber-50/80 dark:bg-amber-950/30',
    iconBg: 'bg-amber-500/15',
    icon: 'text-amber-600 dark:text-amber-400',
    heading: 'text-amber-900 dark:text-amber-200',
    body: 'text-amber-800/90 dark:text-amber-300/80',
    code: 'bg-amber-100/70 dark:bg-amber-900/40',
    cta: 'bg-amber-600 hover:bg-amber-500',
    Icon: AlertTriangle,
  },
  error: {
    border: 'border-rose-300/60 dark:border-rose-700/40',
    bg: 'bg-rose-50/80 dark:bg-rose-950/30',
    iconBg: 'bg-rose-500/15',
    icon: 'text-rose-600 dark:text-rose-400',
    heading: 'text-rose-900 dark:text-rose-200',
    body: 'text-rose-800/90 dark:text-rose-300/80',
    code: 'bg-rose-100/70 dark:bg-rose-900/40',
    cta: 'bg-rose-600 hover:bg-rose-500',
    Icon: XCircle,
  },
};

export default function OnboardingFallbackBanner() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [reason, setReason] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
      try {
        const [leadsRes, statusRes] = await Promise.all([
          api.get('/api/leads/?limit=5'),
          api.get('/api/onboarding/status').catch(() => ({ data: {} })),
        ]);
        const leadsBody = leadsRes.data;
        const items = Array.isArray(leadsBody) ? leadsBody : (leadsBody?.leads || leadsBody?.items || []);
        const realLeads = items.filter(l => !l?.is_demo);
        const demoLeads = items.filter(l => l?.is_demo);
        const hasOnlyDemo = demoLeads.length > 0 && realLeads.length === 0;
        if (!hasOnlyDemo) return;

        const sd = statusRes.data || {};
        const lastJob = sd.last_job || sd.job || null;
        const fallbackReason = lastJob?.fallback_reason;

        // Only render if we have a structured reason. Without one, the
        // banner has nothing useful to say — better silent than vague.
        if (!fallbackReason || !fallbackReason.code) return;
        // Don't nag users who explicitly skipped — their choice.
        if (fallbackReason.code === 'user_skipped') return;

        const storedUrl = lastJob?.website_url || lastJob?.inputs?.website_url || sd.website_url || '';
        if (!cancelled) {
          setWebsiteUrl(storedUrl);
          setReason(fallbackReason);
          setShow(true);
        }
      } catch (_e) { /* silent — never block the dashboard */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await api.post('/api/onboarding/rerun');
      const url = res?.data?.website_url || websiteUrl;
      toast.success('Onboarding reset — let\'s try again.');
      if (url) {
        try { localStorage.setItem('onboarding_prefill_url', url); } catch (_e) { /* ignore */ }
      }
      setTimeout(() => navigate('/onboarding', { replace: true }), 600);
    } catch (e) {
      toast.error('Could not reset onboarding. Please contact support.');
      setRetrying(false);
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_e) { /* ignore */ }
    setShow(false);
  };

  if (!show || !reason) return null;

  const styles = SEVERITY_STYLES[reason.severity] || SEVERITY_STYLES.warning;
  const { Icon } = styles;

  return (
    <div
      className={`rounded-xl border ${styles.border} ${styles.bg} backdrop-blur-sm p-4 sm:p-5`}
      data-testid="onboarding-fallback-banner"
      data-fallback-code={reason.code}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`shrink-0 mt-0.5 h-9 w-9 rounded-lg ${styles.iconBg} flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${styles.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm sm:text-base font-semibold ${styles.heading}`} data-testid="onboarding-fallback-title">
            {reason.title}
          </h3>
          <p className={`mt-1 text-xs sm:text-sm ${styles.body} leading-relaxed`} data-testid="onboarding-fallback-message">
            {reason.message}
            {websiteUrl && reason.code?.startsWith('website_') && (
              <>
                {' '}
                <code className={`px-1 py-0.5 rounded ${styles.code} text-[11px]`}>{websiteUrl}</code>
              </>
            )}
          </p>
          {reason.hint && (
            <p className={`mt-2 text-xs ${styles.body} leading-relaxed italic`} data-testid="onboarding-fallback-hint">
              Tip: {reason.hint}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={handleRetry}
              disabled={retrying}
              className={`h-8 text-white ${styles.cta}`}
              data-testid="onboarding-fallback-retry-btn"
            >
              {retrying ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              {retrying ? 'Resetting…' : 'Re-run Onboarding'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className={`h-8 ${styles.heading} hover:bg-black/5 dark:hover:bg-white/5`}
              data-testid="onboarding-fallback-dismiss-btn"
            >
              Continue manually
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
